import React from "react";
import ReactDOM from "react-dom";
// OUI theme stylesheets are loaded as toggleable <link> tags in public/index.html
// and switched at runtime by ColorModeProvider (OUI 1.19 has no EuiProvider).
import * as Sentry from "@sentry/react";
import packageJson from "./../package.json";
// Theme-aware tokens for the marketing homepage sections (light + dark).
import "./homepage/homepage-theme.css";

// Make sure all locales are loaded
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/pt";
import "moment/locale/es";
import "moment/locale/pl";
import "moment/locale/ru";
import "moment/locale/nl";
import "moment/locale/en-gb";
import "moment/locale/de-ch";
import "moment/locale/de-at";
import { BrowserRouter as Router } from "react-router-dom";
import { AnketaI18nProvider } from "./internationalization";
import { ToastProvider } from "./notifications";
import { Navigation } from "./navigation";
import { AnketaAuth } from "./auth/context";
import { ConsentProvider, CookieBanner } from "./cookie";
import { ColorModeProvider } from "./theme";

/* eslint-disable no-restricted-globals */

// "ResizeObserver loop completed with undelivered notifications" is benign: the
// browser reports it when a ResizeObserver callback changes layout, and OUI's
// accordions / combo boxes trigger it routinely. The webpack-dev-server overlay
// shows it as a crash, so swallow exactly this message in development. The
// capture-phase listener runs before the overlay's own window "error" handler.
const RESIZE_OBSERVER_NOISE = /ResizeObserver loop/;
if (process.env.NODE_ENV === "development") {
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (RESIZE_OBSERVER_NOISE.test(event.message ?? "")) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );
}

Sentry.init({
  dsn: "https://a823b17c9fbba452dde7dd66df518676@sentry.genesis.exanio.cloud/18",
  sendDefaultPii: true,
  release: packageJson.version,
  debug: false,
  // Same benign browser notice - not worth an error event (plus the 100%
  // on-error session replay it would trigger).
  ignoreErrors: [RESIZE_OBSERVER_NOISE],
  integrations: [
    Sentry.browserTracingIntegration({}),
    Sentry.browserProfilingIntegration(),
    Sentry.browserSessionIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

/* eslint-enable */

const mockCookieData = [
  { name: "AUTH_SESSION", domain: "keycloak.autonubil.net", session: true },
  { name: "_fbp", domain: "microsoftonline.com", session: false },
];

ReactDOM.render(
  <ConsentProvider cookieData={mockCookieData}>
    <Router>
      <ColorModeProvider>
        <AnketaI18nProvider>
          <AnketaAuth>
            <ToastProvider>
              <CookieBanner
                privacyPolicyUrl="/privacy"
                companyName="autonubil"
                cookieData={mockCookieData}
              />
              <Navigation />
            </ToastProvider>
          </AnketaAuth>
        </AnketaI18nProvider>
      </ColorModeProvider>
    </Router>
  </ConsentProvider>,
  document.getElementById("root"),
);

export * from "./admin";
export * from "./assets";
export * from "./auth";
export * from "./notifications";
export * from "./pages";
export * from "./navigation";
export * from "./internationalization";
export * from "./theme";
export * from "./api";
export * from "./helper";
